/* @flow */

import * as React from 'react';
import { every, get, head, isEmpty, some } from 'lodash/fp';
import styled from '@emotion/styled';

import t from 'app/utils/translate';
import PlayerPositionTab from './PlayerPositionTab';

const allFilled = every('filled');
const containsError = some('hasError');

type Props = {
  autoAdvance?: boolean,
  onSelect: (string | null) => void,
  games: Array<Object>,
  requiredSelections?: number,
  selectedGame?: string,
  showAllTab?: boolean,
  showFilled?: boolean,
  showSelectionFeedback?: boolean,
  totalSelected?: number
};

const PlayerGamesNav = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  overflow: 'scroll',
  position: 'relative',
  WebkitOverflowScrolling: 'touch',
  [theme.mediaQuery.desktopAndUp]: {
    overflow: 'hidden'
  }
}));

class PlayerGamesBar extends React.PureComponent<Props> {
  props: Props;

  static defaultProps = {
    autoAdvance: false,
    requiredSelections: 0,
    selectedGame: undefined,
    showFilled: true,
    showAllTab: false,
    showSelectionFeedback: false,
    totalSelected: 0
  };

  componentDidMount() {
    const { games, onSelect, selectedGame, showAllTab } = this.props;

    if (!showAllTab && !selectedGame) {
      onSelect(get('id', head(games)));
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { autoAdvance, onSelect, games, selectedGame: gameId } = this.props;
    const { games: prevGames, selectedGame: prevGameId } = prevProps;

    if (
      !autoAdvance ||
      !gameId ||
      !Array.isArray(games) ||
      !Array.isArray(prevGames) ||
      games.length <= 1 ||
      gameId !== prevGameId
    ) {
      return;
    }

    const selectedGame = games.find(pos => pos.id === gameId);
    const prevSelectedGame = prevGames.find(pos => pos.id === gameId);

    if (
      !isEmpty(selectedGame) &&
      !isEmpty(prevSelectedGame) &&
      selectedGame.filled &&
      !prevSelectedGame.filled
    ) {
      const nextGame = games.find(pos => pos.id !== selectedGame.id && !pos.filled);
      if (nextGame) {
        onSelect(nextGame.id);
      }
    }
  }

  render() {
    const {
      onSelect,
      games,
      requiredSelections,
      selectedGame,
      showAllTab,
      showFilled,
      showSelectionFeedback,
      totalSelected
    } = this.props;

    return (
      <PlayerGamesNav>
        {showAllTab && (
          <PlayerPositionTab
            enabled={!selectedGame}
            filled={requiredSelections === totalSelected && allFilled(games)}
            hasError={containsError(games)}
            label={t('All')}
            max={requiredSelections}
            min={requiredSelections}
            onSelect={() => onSelect(null)}
            selected={totalSelected}
            showFilled={showFilled}
            showSelectionFeedback={showSelectionFeedback}
          />
        )}
        {games.map(game => (
          <PlayerPositionTab
            key={game.id}
            enabled={game.id === selectedGame}
            filled={game.filled}
            hasError={game.hasError}
            label={game.label}
            max={game.max}
            min={game.min}
            onSelect={() => onSelect(game.id)}
            selected={game.selected}
            showFilled={showFilled}
            showSelectionFeedback={showSelectionFeedback}
          />
        ))}
      </PlayerGamesNav>
    );
  }
}

export default PlayerGamesBar;
